import { useEffect, useState } from "react";
import { FieldValues, FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import Button from "../../atoms/Button/Button";
import styles from "./HAQ.module.scss";
import Accordion from "../../components/Accordian/Accordian";
import { ApiService } from "../../services/apiServices";
import {
  HAQHeading,
  HAQSections,
  Page,
  StorageEnums,
} from "../../enums/storageEnums";
import { toast } from "react-toastify";
import GlobalLoader from "../../atoms/GlobalLodaer/GlobalLoader";
import { getSignedUrl, uploadImage } from "../../constants/commonFunctions";
import { haqSchema } from "./haq.schema";
import HAQSection from "../../components/HAQSection/HAQSection";

export interface APIResponse {
  id: string;
  section: string;
  key: string;
  content: string;
  page: string;
  order: number;
  isPublish: boolean;
}

const HaveAQuestion = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingForPublish, setLoadingForPublish] = useState<boolean>(false);
  const [isFormEdited, setIsFormEdited] = useState<boolean>(false);
  const [haqPageData, setHAQPageData] = useState<APIResponse[]>([]);
  const [isPublish, setIsPublish] = useState<boolean>(true);
  const apiService = new ApiService();
  const localStorageUser = JSON.parse(
    localStorage.getItem(StorageEnums.CREDENTIALS) || '{"accessToken": ""}'
  );

  const methods = useForm({
    resolver: yupResolver(haqSchema),
  });
  const { handleSubmit, formState } = methods;

  const CloudFrontURL = process.env.REACT_APP_CLOUDFRONT_URL;

  useEffect(() => {
    // Listen for changes in any form field
    setIsFormEdited(formState.isDirty);
  }, [formState]);
  const gethaqPageData = async () => {
    const payload = {
      url: `pages/${Page.HAQ}`,
      headerToken: localStorageUser.data.accessToken,
    };
    const response = await apiService.get(payload);
    const data = response?.data?.result;
    setHAQPageData(data);

    if (data.length) {
      setIsPublish(data[0].isPublish);
      const resetObject: { [key: string]: any } = {};
      data.forEach((item: any) => {
        resetObject[item.key] = item.content;
      });
      methods.reset(resetObject);
    } else {
      setIsPublish(true);
    }
  };

  useEffect(() => {
    gethaqPageData();
  }, []);

  const handleSave = async (data: FieldValues) => {
    if (!haqPageData.length) {
      try {
        setLoading(true);

        // Prepare data to upload
        const dataToUpload: any[] = [
          // Banner section
          {
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_TITILE,
            content: data[HAQHeading.HAQ_TITILE],
            page: Page.HAQ,
          },
          {
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_DESCRIPTION,
            content: data[HAQHeading.HAQ_DESCRIPTION],
            page: Page.HAQ,
          },
          {
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_BUTTON,
            content: data[HAQHeading.HAQ_BUTTON],
            page: Page.HAQ,
          },
        ];

        const payload = {
          data: { sections: dataToUpload },
          url: `pages/${Page.HAQ}`,
          headerToken: localStorageUser.data.accessToken,
        };
        await apiService.post(payload);
        methods.reset();
        gethaqPageData();

        toast.success("Data uploaded successfully");
      } catch (error: any) {
        console.error(error, "Error occurred while uploading images");
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        setLoading(true);

        // Prepare data to upload
        const dataToUpload: any[] = [
          // Banner section

          {
            id: haqPageData.find((item) => item.key === HAQHeading.HAQ_TITILE)
              ?.id,
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_TITILE,
            content: data[HAQHeading.HAQ_TITILE],
            page: Page.HAQ,
          },
          {
            id: haqPageData.find(
              (item) => item.key === HAQHeading.HAQ_DESCRIPTION
            )?.id,
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_DESCRIPTION,
            content: data[HAQHeading.HAQ_DESCRIPTION],
            page: Page.HAQ,
          },
          {
            id: haqPageData.find((item) => item.key === HAQHeading.HAQ_BUTTON)
              ?.id,
            section: HAQSections.HAQ_SECTION,
            key: HAQHeading.HAQ_BUTTON,
            content: data[HAQHeading.HAQ_BUTTON],
            page: Page.HAQ,
          },
        ];

        const payload = {
          data: { sections: dataToUpload },
          url: `pages/${Page.HAQ}`,
          headerToken: localStorageUser.data.accessToken,
        };
        await apiService.post(payload);
        gethaqPageData();
        toast.success("Data updated successfully");
      } catch (error: any) {
        console.error(error, "Error occurred while uploading images");
        toast.error(error?.response?.data?.message);
      } finally {
        setLoading(false);
      }
    }
  };

  const handlePublish = async () => {
    setLoadingForPublish(true);
    try {
      const payload = {
        url: `publish/pages/${Page.HAQ}`,
        headerToken: localStorageUser.data.accessToken,
      };
      await apiService.post(payload);
      toast.success("Published successfully");
      gethaqPageData();
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    } finally {
      setLoadingForPublish(false);
    }
  };

  return (
    <div className={`${styles.home}`}>
      <div
        className={`flex align-center justify-between ${styles.home__header}`}
      >
        <h2>Have a question ?</h2>
        <div className={`flex align-center ${styles.home__button}`}>
          <Button
            label="Save"
            type="submit"
            buttonClass="md"
            loading={loading}
            disabled={loading || !isFormEdited}
            onClick={handleSubmit(handleSave)}
          />

          <Button
            label="Publish"
            type="submit"
            buttonClass="md"
            loading={loadingForPublish}
            disabled={isPublish}
            onClick={handlePublish}
          />
        </div>
      </div>

      <form
        onSubmit={(event: React.FormEvent<HTMLFormElement>) =>
          event.preventDefault()
        }
        noValidate
      >
        <div className={`flex-column ${styles.home__form}`}>
          <FormProvider {...methods}>
            <Accordion title="Have a question ?">
              <HAQSection />
            </Accordion>
          </FormProvider>
        </div>
      </form>
      {loading && <GlobalLoader />}
    </div>
  );
};

export default HaveAQuestion;
